import { OHIVE_COLORS } from '@constants/global.js';
import { UIButton, UIDiv, UIElement } from '@ui';

export default class Tabs extends UIDiv {
  constructor(
    tabArray = [
      {
        display: null,
        title: '',
        icon: null,
        content: new UIElement(),
        bgColor: OHIVE_COLORS.light,
        callback: null,
      },
    ],
    tabContentClass = '',
    styleType = 'underline', // Choices: 'underline' or 'filled'
    disableMinHeight = false, // NEW PARAM
  ) {
    super();

    this.setClass(
      `${disableMinHeight ? '' : 'min-h-[20rem]'} h-full w-full max-w-full relative`,
    );
    this.tabArray = tabArray;
    this.styleType = styleType;
    this.tabButtonInstances = [];

    this.tabList = new UIDiv();
    this.tabContent = new UIDiv();
    this.tabIndicator = new UIDiv();

    this.styles = {
      underline: {
        tabList:
          'flex justify-start items-end gap-2 max-w-full px-[2px] relative overflow-x-auto shadow-[inset_0_-1px_0_var(--o-hive-light-80)] whitespace-nowrap',
        tabButtonBase:
          'px-4 py-[1px] pb-4 flex items-center gap-2 transition-colors duration-150 outline-none focus-visible:ring-2 focus-visible:ring-[var(--o-hive-primary)]',
        tabButtonInactive:
          'text-[var(--o-hive-neutral-60)] hover:text-[var(--o-hive-primary)]',
        tabButtonActive: 'text-[var(--o-hive-primary)]',
      },
      filled: {
        tabList:
          'flex justify-start items-stretch gap-2 max-w-full h-auto px-1 py-1 relative overflow-x-auto bg-gray-100 rounded-lg whitespace-nowrap',
        tabButtonBase:
          'flex-1 justify-center px-4 py-2.5 rounded-md flex items-center gap-2 transition-colors duration-150 outline-none focus-visible:ring-2 focus-visible:ring-[var(--o-hive-primary)] focus-visible:ring-offset-1 focus-visible:ring-offset-gray-100',
        tabButtonInactive:
          'text-gray-500 hover:bg-gray-200 hover:text-gray-700',
        tabButtonActive:
          'bg-white text-black shadow-[0_-1px_3px_rgba(0,0,0,0.07),0_1px_2px_rgba(0,0,0,0.03)] relative',
      },
    };

    const currentStyleConfig = this.styles[this.styleType];
    this.tabList.setClass(currentStyleConfig.tabList);
    this.tabContent.setClass(tabContentClass);

    if (this.styleType === 'underline') {
      this.tabIndicator.setClass('tab-indicator ');
    }

    this.tabArray.forEach((tab, index) => {
      const tabButton = new UIButton();
      this.tabButtonInstances.push(tabButton);
      tabButton.setClass(
        `${currentStyleConfig.tabButtonBase} ${currentStyleConfig.tabButtonInactive}`,
      );
      tabButton.setTextContent(tab.title);

      if (tab.icon) {
        tabButton.add(tab.icon);
        tabButton.dataset('iconAdded', 'true');
      }

      // When a tab button is clicked, perform the tab change and execute its callback
      tabButton.onClick(() => {
        this.performTabChange(index, tab.callback);
      });

      this.tabList.add(tabButton);
    });

    if (this.styleType === 'underline') {
      this.tabList.add(this.tabIndicator);
    }

    this.add(this.tabList);
    this.add(this.tabContent);

    this.activeTabIndex = 0; // Default to the first tab
    this._updateTabUI(this.activeTabIndex);

    return this;
  }

  performTabChange(index, callback) {
    this._updateTabUI(index);

    if (this.activeTabIndex === index && typeof callback === 'function') {
      callback();
    }
  }

  _updateTabUI(index) {
    if (index < 0 || index >= this.tabArray.length) {
      return;
    }

    const currentStyleConfig = this.styles[this.styleType];
    const newActiveButtonInstance = this.tabButtonInstances[index];
    if (!newActiveButtonInstance) {
      // Should always find a button if index is valid
      return;
    }
    const tabData = this.tabArray[index];

    this.tabButtonInstances.forEach((buttonInstance, i) => {
      const isActive = i === index;
      buttonInstance.setClass(
        `${currentStyleConfig.tabButtonBase} ${isActive ? currentStyleConfig.tabButtonActive : currentStyleConfig.tabButtonInactive}`,
      );
    });

    this.tabContent.setInnerHTML('');
    const contentElement = tabData.content;
    if (contentElement instanceof UIElement) {
      contentElement.addClass('w-full h-full');
      this.tabContent.add(contentElement);
    }

    // Update the position and width of the underline indicator if using 'underline' style.
    if (this.styleType === 'underline') {
      requestAnimationFrame(() => {
        const btnDomElement = newActiveButtonInstance.dom;
        const listDomElement = this.tabList.dom;

        if (
          btnDomElement &&
          listDomElement &&
          listDomElement.offsetParent !== null
        ) {
          const btnRect = btnDomElement.getBoundingClientRect();
          const listRect = listDomElement.getBoundingClientRect();

          if (listRect.width > 0 && btnRect.width > 0) {
            const tabLength = btnRect.width;
            const tabPos =
              btnRect.left - listRect.left + listDomElement.scrollLeft;

            this.tabIndicator.setWidth(tabLength + 'px');
            this.tabIndicator.setLeft(tabPos + 'px');
          }
        }
      });
    }
    this.activeTabIndex = index;
  }

  setActiveTab(index) {
    if (index >= 0 && index < this.tabArray.length) {
      const tabData = this.tabArray[index];
      this.performTabChange(index, tabData.callback);
    } else {
      console.warn(`Tabs.setActiveTab: Index ${index} is out of bounds.`);
    }
  }

  setDisabled(disabled, tabIndex = null) {
    const buttonsToDisable =
      tabIndex !== null
        ? [this.tabButtonInstances[tabIndex]].filter(Boolean)
        : this.tabButtonInstances;

    buttonsToDisable.forEach(tabButton => {
      if (disabled) {
        if (!tabButton._originalOnClick) {
          tabButton._originalOnClick = tabButton._onClick;
        }
        tabButton._onClick = () => {};
        tabButton.addClass('opacity-50 cursor-not-allowed pointer-events-none');
      } else {
        if (tabButton._originalOnClick) {
          tabButton._onClick = tabButton._originalOnClick;
        }
        tabButton.removeClass(
          'opacity-50 cursor-not-allowed pointer-events-none',
        );
      }
    });
  }
}
